import { createAsyncThunk } from '@reduxjs/toolkit';
import lodash from 'lodash';
import {
  getCurrentSavedCard,
  getEnableDynamicPaymentGatewayConfig,
  getLocation,
  getLoyaltyTopUpValue,
  getMember,
  getRememberCreditCard,
  getSelectedPaymentMethods,
  getReceiptType,
  getGatewayType,
  getOrderingProvider,
} from '../selectors';
import getSaleBody from '../selectors/getSaleBody';
import { AuthenticationMethod } from '../utils/Api';
import smoothPickupTime from '../utils/ordering/smoothPickupTime';
import { PAYMENT_METHOD } from '../constants/paymentMethod';
import { SALE_TYPE } from '../constants/saleType';
import { PAYMENT_GATEWAY_TYPES } from '../constants';
import { ORDERING_PROVIDER } from '../constants/orderingProvider';

const PAYMENT_GATEWAY_METHODS = [
  PAYMENT_METHOD.APPLE_PAY,
  PAYMENT_METHOD.GOOGLE_PAY,
  PAYMENT_METHOD.CREDIT_CARD,
  PAYMENT_METHOD.EFTPOS,
  PAYMENT_METHOD.SAVED_CARD,
  PAYMENT_METHOD.ALIPAY,
];

const getPaymentInfoBody = (
  gatewayType: string,
  payment_gateway: SubPayment,
  currentSavedCardToken: string | null,
  enableDynamicPaymentGatewayConfig: boolean,
  member: Member | undefined,
  rememberCreditCard: boolean,
) => {
  if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
    return {
      ...(payment_gateway.method === PAYMENT_METHOD.SAVED_CARD
        ? { saved_pg_token: currentSavedCardToken }
        : { id: payment_gateway.token }),
      remember_pg_token: rememberCreditCard || undefined,
      last_four: rememberCreditCard ? payment_gateway.lastFour : undefined,
      useIntents: enableDynamicPaymentGatewayConfig,
      paymentMethodType: payment_gateway.method,
    };
  }

  if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
    return payment_gateway.method === PAYMENT_METHOD.SAVED_CARD
      ? { pay_method_id: currentSavedCardToken }
      : { save_pay_method: member && rememberCreditCard };
  }
};

export const constructSalePaymentRequest = createAsyncThunk(
  '$constructSalePaymentRequest',
  async (
    data: {
      authenticationMethod?: AuthenticationMethod;
      route?: 'topup' | 'checkout';
      receiptType?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      let body: any = {};
      let path = '';
      const { route = 'checkout', authenticationMethod = 'none' } = data;

      const receiptType = getReceiptType(getState() as EntireFrontendState);
      const orderingProvider = getOrderingProvider(
        getState() as EntireFrontendState,
      );

      const total = getLoyaltyTopUpValue(getState() as EntireFrontendState);
      const member = getMember(getState() as EntireFrontendState);
      const gatewayType = getGatewayType(getState() as EntireFrontendState);
      const selectedPaymentMethods = getSelectedPaymentMethods(
        getState() as EntireFrontendState,
      );
      const payment_gateway = selectedPaymentMethods.filter(options =>
        PAYMENT_GATEWAY_METHODS.includes(options.method),
      )[0];

      const currentSavedCardToken = getCurrentSavedCard(
        getState() as EntireFrontendState,
      );
      const enableDynamicPaymentGatewayConfig =
        getEnableDynamicPaymentGatewayConfig(getState() as EntireFrontendState);

      const rememberCreditCard =
        getRememberCreditCard(getState() as EntireFrontendState) &&
        payment_gateway?.method === PAYMENT_METHOD.CREDIT_CARD;

      if (route === 'checkout') {
        const baseBody = getSaleBody(getState() as EntireFrontendState);

        body = { ...baseBody };

        if (baseBody.Payments) {
          if (baseBody.Payments.length === 1) {
            const { Payments: _, ...baseBodyWithoutPayments } = baseBody;
            body = {
              ...baseBodyWithoutPayments,
              ...baseBody.Payments[0],
              printer_settings: {
                KioskPrintReceiptType: receiptType,
              },
            };
          } else if (baseBody.Payments.length > 1) {
            body = {
              ...baseBody,
              MultiplePaymentMode: 'PART',
              printer_settings: {
                KioskPrintReceiptType: receiptType,
              },
            };
          }
        } else {
          throw new Error('payment method is undefined');
        }

        if (body.SaleType === SALE_TYPE.TABLE_ORDER) {
          body.PickupTime = 'ASAP';
        }

        if (body.PickupTime != undefined) {
          const location = getLocation(getState() as EntireFrontendState);

          body.PickupTime = smoothPickupTime(
            lodash.get(baseBody, 'PickupTime'),
            location!.averageOrderWaitTime,
          );
        }

        if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
          path = {
            trusted: '/api/v1/authsale2',
            member: '/api/v1/sale',
            none: '/api/v1/sale/nonmember',
          }[authenticationMethod];
        }

        if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
          path = '/api/v1/sale/token_sale';
        }

        if (gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY) {
          path = '/api/v2/online-payments/sales?mode=order';
        }

        // Kiosk ordering provider only uses authsale2
        if (orderingProvider === ORDERING_PROVIDER.KIOSK) {
          path = '/api/v1/authsale2';
        }
      }

      // Polygon-mobile uses it but polygon-web-ordering has its own thunk for topup as web also have to handle giftcards
      if (route === 'topup') {
        body = {
          ...body,
          HCTopupAmount: total,
          recid_member: member?.id,
          PaymentType: 'cc',
          PaymentInfo: getPaymentInfoBody(
            gatewayType,
            payment_gateway,
            currentSavedCardToken,
            enableDynamicPaymentGatewayConfig,
            member,
            rememberCreditCard,
          ),
        };

        path =
          gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
            ? 'api/v1/sale/topup/token_sale'
            : '/api/v1/sale';
      }

      return {
        body,
        path,
      };
    } catch (e) {
      console.error('Cant construct payment request', { e });
      return rejectWithValue(e);
    }
  },
);
